import React, { useRef, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../context/AuthContext";
import { uploadListingImage } from "../lib/upload";
import SafeImage from "./SafeImage";

/**
 * Business logo + gallery manager. A listing is only publicly visible with a
 * logo, 3-10 gallery photos, and a 100+ character description — this panel is
 * where the owner fixes exactly that.
 */
export default function BusinessMediaManager({ user, authHeader, status, onChanged }) {
  const logoRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const gallery = status?.gallery_images || user?.gallery_images || [];
  const min = status?.requirements?.gallery_min ?? 3;
  const max = status?.requirements?.gallery_max ?? 10;

  const upload = async (file, kind) => {
    if (!file) return;
    setBusy(true);
    setNotice("");
    try {
      await uploadListingImage(file, kind, authHeader);
      toast.success(kind === "logo" ? "Logo updated" : "Photo added");
      if (onChanged) await onChanged();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Upload failed";
      setNotice(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const removePhoto = async (id) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/business/gallery/${id}`, { headers: authHeader() });
      toast.success("Photo removed");
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove that photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border p-6" style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}
         data-testid="business-media-manager">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-xl" style={{ color: "var(--gf-text)" }}>Photos</h3>
          <p className="text-sm mt-1" style={{ color: "var(--gf-text-muted)" }}>
            One logo, plus {min}–{max} gallery photos. Gallery photos can also be used as service cover images.
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <SafeImage
          src={user?.profile_image_url}
          alt="Business logo"
          testId="logo-preview"
          className="w-16 h-16 rounded-full object-cover border"
          fallback={
            <div className="w-16 h-16 rounded-full border flex items-center justify-center text-xs text-center px-1"
                 style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)" }} data-testid="logo-empty">
              {user?.profile_image_url ? "Image unavailable" : "No logo"}
            </div>
          }
        />
        <div>
          <input ref={logoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                 data-testid="logo-file-input"
                 onChange={(e) => { upload(e.target.files?.[0], "logo"); e.target.value = ""; }} />
          <button type="button" disabled={busy} data-testid="upload-logo-btn" onClick={() => logoRef.current?.click()}
                  className="rounded-full px-5 py-2 text-sm font-medium disabled:opacity-50"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
            {user?.profile_image_url ? "Replace logo" : "Upload logo"}
          </button>
          <div className="text-[11px] mt-1" style={{ color: "var(--gf-text-muted)" }}>JPEG, PNG, or WebP · 5MB max</div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>
            Gallery
            <span className="ml-2" data-testid="gallery-count"
                  style={{ color: gallery.length >= min ? "var(--gf-accent)" : "var(--gf-text-muted)" }}>
              {gallery.length}/{min} minimum
            </span>
          </div>
          <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                 data-testid="gallery-file-input"
                 onChange={(e) => { upload(e.target.files?.[0], "gallery"); e.target.value = ""; }} />
          <button type="button" disabled={busy || gallery.length >= max} data-testid="upload-gallery-btn"
                  onClick={() => galleryRef.current?.click()}
                  className="rounded-full border px-4 py-1.5 text-sm disabled:opacity-50"
                  style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
            + Add photo
          </button>
        </div>
        {notice && (
          <div data-testid="gallery-duplicate-msg" className="mt-2 rounded-lg border px-3 py-2 text-xs"
               style={{ borderColor: "var(--gf-accent)", background: "var(--gf-surface-2)", color: "var(--gf-text)" }}>
            {notice}
          </div>
        )}
        {gallery.length === 0 ? (
          <div className="mt-3 rounded-xl border border-dashed p-6 text-center text-sm"
               style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)" }} data-testid="gallery-empty">
            No photos yet. Add at least {min} to go live.
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="gallery-grid">
            {gallery.map((g, i) => (
              <div key={g.id || i} className="relative rounded-lg overflow-hidden border aspect-square"
                   style={{ borderColor: "var(--gf-border)" }} data-testid={`gallery-item-${i}`}>
                <SafeImage src={g.url} alt={`Gallery photo ${i + 1}`} className="w-full h-full object-cover" />
                <button type="button" disabled={busy} onClick={() => removePhoto(g.id)}
                        data-testid={`gallery-remove-${i}`} aria-label="Remove photo"
                        className="absolute top-1 right-1 w-6 h-6 rounded-full text-xs flex items-center justify-center"
                        style={{ background: "rgba(0,0,0,0.65)", color: "#fff" }}>
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
